import React, { useEffect, useState } from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';
import { getFirestore, doc, updateDoc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import OnboardingFlow from '../components/OnboardingFlow';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';

export default function OnboardingScreen() {
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const [config, setConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [userTier, setUserTier] = useState('standard');

  // Determine tier from user's Firestore doc
  useEffect(() => {
    if (!user?.uid) return;
    const db = getFirestore();
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.userType === 'admin' || d.alphaAccess) setUserTier('alpha');
      else if (d.betaAccess) setUserTier('beta');
      else setUserTier('standard');
    });
    return () => unsub();
  }, [user?.uid]);

  // Load live onboarding config from Firestore (admin-editable)
  useEffect(() => {
    const db = getFirestore();
    const configRef = doc(db, 'site_config', 'onboarding');
    const unsub = onSnapshot(configRef, (snap) => {
      const data = snap.exists() ? snap.data() : null;
      setConfig(data);
      // If onboarding has been disabled by admin, skip it for non-admins
      if (data?.enabled === false) {
        navigateTo('Home');
        return;
      }
      setConfigLoaded(true);
    }, () => {
      setConfigLoaded(true); // proceed with defaults on error
    });
    return () => unsub();
  }, [navigateTo]);

  const saveAndNavigate = async (prefs) => {
    if (!user?.uid) {
      navigateTo('Home');
      return;
    }
    const db = getFirestore();
    const userRef = doc(db, 'users', user.uid);

    const selectedBooks = prefs.myBooks || [];
    const profileUpdate = {
      onboardingCompleted: true,
      onboardingCompletedAt: prefs.onboardingCompletedAt || new Date().toISOString(),
      onboardingSkipped: false,
      // Map onboarding selections into existing preference fields
      'preferences.selectedSportsbooks': selectedBooks,
      'preferences.unitSize': prefs.unitSize || 10,
      'preferences.evThreshold': prefs.evThreshold || 0,
      'preferences.selectedSports': prefs.selectedSports || [],
      'preferences.betExperience': prefs.betExperience || 'intermediate',
      // Default to "My Books" sort if the user selected any sportsbooks
      'preferences.defaultBookSort': selectedBooks.length > 0 ? 'mybooks' : 'ev',
      bettingProfile: prefs.bettingProfile || null,
    };

    try {
      await updateDoc(userRef, profileUpdate);
    } catch {
      // If doc doesn't exist yet, create it
      await setDoc(userRef, {
        uid: user.uid,
        ...profileUpdate,
      }, { merge: true });
    }
    navigateTo('Home');
  };

  const handleSkip = async () => {
    if (user?.uid) {
      const db = getFirestore();
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          onboardingSkipped: true,
          onboardingSkippedAt: new Date().toISOString(),
        });
      } catch {}
    }
    navigateTo('Home');
  };

  // Track per-step progress in Firestore for admin analytics
  const handleStepChange = async (stepIndex) => {
    if (!user?.uid) return;
    const db = getFirestore();
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        onboardingStep: stepIndex,
        onboardingStepUpdatedAt: new Date().toISOString(),
      });
    } catch {}
  };

  if (!configLoaded) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator color="#8B5CF6" size="large" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <OnboardingFlow
        config={config}
        onComplete={saveAndNavigate}
        onSkip={handleSkip}
        onStepChange={handleStepChange}
        userTier={userTier}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#0a0a0f' },
});
